import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Typography,
  IconButton,
  Stack,
  Box,
  styled,
  TypographyProps,
  useTheme
} from "@mui/material";
import { useState, useRef, useEffect, MouseEvent } from "react";

import { Warning24Px } from "../icons/autostoreIcons/icons24px";
import { LeafIcon } from "../icons/leafIcon/LeafIcon";

import { Sku } from "./Sku";
import { UpcList } from "./UpcList";

type Props = {
  productName: string;
  sku?: string;
  upcs?: string[];
  warning?: boolean;
  sanitize?: boolean;
};

const OuterWrapper = styled(Box, {
  shouldForwardProp: (prop) => !["isExpanded"].includes(prop as string)
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  position: "relative",
  zIndex: isExpanded ? 1 : "auto",
  minHeight: theme.spacing(23.5),
  margin: theme.spacing(-2)
}));

const FieldsContainer = styled(Stack, {
  shouldForwardProp: (prop) => !["isExpanded"].includes(prop as string)
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  position: isExpanded ? "absolute" : "relative",
  top: isExpanded ? theme.spacing(2) : "auto",
  padding: isExpanded ? 0 : theme.spacing(2),
  gap: isExpanded ? theme.spacing(2) : 0,
  backgroundColor: theme.palette.common.white,
  flexDirection: "column"
}));

const ClickableRow = styled(Stack, {
  shouldForwardProp: (prop) =>
    !["requiresTruncation", "isExpanded"].includes(prop as string)
})<{ isExpanded: boolean; requiresTruncation: boolean }>(
  ({ theme, requiresTruncation, isExpanded }) => ({
    cursor: requiresTruncation || isExpanded ? "pointer" : "auto",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  })
);

const ProductName = styled(Typography, {
  shouldForwardProp: (prop) => !["isExpanded"].includes(prop as string)
})<{ isExpanded: boolean } & TypographyProps>(({ theme, isExpanded }) => ({
  marginLeft: isExpanded ? theme.spacing(2) : 0,
  wordBreak: "break-word"
}));

const Icons = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(1),
  flexDirection: "row"
}));

const ExpandButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    !["isExpanded", "warning"].includes(prop as string)
})<{ isExpanded: boolean; warning: boolean }>(
  ({ theme, isExpanded, warning }) => ({
    marginRight: isExpanded && !warning ? theme.spacing(2) : 0,
    transform: `rotate(${isExpanded ? 180 : 0}deg)`,
    transition: "transform 0.2s",
    alignSelf: "center",
    padding: 0
  })
);

const WarningIcon = styled(Warning24Px, {
  shouldForwardProp: (prop) => prop !== "isExpanded"
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  marginRight: isExpanded ? theme.spacing(2) : 0,
  alignSelf: isExpanded ? "start" : "center",
  paddingTop: isExpanded ? "5px" : 0
}));

const StyledLeafIcon = styled(LeafIcon, {
  shouldForwardProp: (prop) => prop !== "isExpanded"
})<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  marginRight: isExpanded ? theme.spacing(2) : 0,
  alignSelf: isExpanded ? "start" : "center",
  paddingTop: isExpanded ? "5px" : 0
}));

/**
 * Renders the product name, truncated to one line, inside the ProductCard
 * component.
 *
 * The text is truncated using the `noWrap` prop on the MUI Typography component.
 *
 * An expand button is also rendered when the width of the product name requires
 * truncation. When clicked it will expand downwards showing the full product name.
 *
 * This component can also render the SKU and UPC. The reason for this is we
 * want the product name to expand downwards, covering the content below it,
 * while still being able to see the SKU and UPC.
 */
export function TruncatedProductName({
  productName,
  sku,
  upcs,
  warning,
  sanitize
}: Props) {
  const [requiresTruncation, setRequiresTruncation] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const { palette } = useTheme();

  const checkRequiresTruncation = () => {
    if (textRef.current) {
      const totalWidth = textRef.current.scrollWidth;
      const visibleWidth = textRef.current.clientWidth;
      setRequiresTruncation(visibleWidth < totalWidth);
    }
  };

  useEffect(() => {
    checkRequiresTruncation();
    const resizeObserver = new ResizeObserver(checkRequiresTruncation);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  const onTextClick = () => {
    if (requiresTruncation || isExpanded) {
      setIsExpanded((prev) => !prev);
    }
  };

  const onIconClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const SkuAndUpc = () => (
    <Stack gap={1}>
      {sku && <Sku sku={sku} sx={{ mx: 0 }} />}
      {upcs?.length && <UpcList upcs={upcs} sx={{ mx: 0 }} />}
    </Stack>
  );

  return (
    <OuterWrapper isExpanded={isExpanded}>
      <FieldsContainer isExpanded={isExpanded}>
        <ClickableRow
          role="button"
          onClick={onTextClick}
          tabIndex={0}
          isExpanded={isExpanded}
          requiresTruncation={requiresTruncation}
        >
          <ProductName
            ref={textRef}
            noWrap={!isExpanded}
            variant="h5"
            isExpanded={isExpanded}
          >
            {productName}
          </ProductName>
          <Icons>
            {(requiresTruncation || isExpanded) && (
              <ExpandButton
                size="large"
                onClick={onIconClick}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} product name`}
                isExpanded={isExpanded}
                warning={!!warning}
              >
                <ExpandMoreIcon fontSize="inherit" />
              </ExpandButton>
            )}
            {warning && (
              <WarningIcon
                fill={palette.productCardSafety.warning}
                isExpanded={isExpanded}
                aria-label="warning"
              />
            )}
            {sanitize && (
              <StyledLeafIcon
                sx={{ color: palette.productCardSafety.sanitize }}
                isExpanded={isExpanded}
                aria-label="sanitize"
              />
            )}
          </Icons>
        </ClickableRow>

        {isExpanded && <SkuAndUpc />}
      </FieldsContainer>

      {!isExpanded && <SkuAndUpc />}
    </OuterWrapper>
  );
}
